import actionTypes from 'constants/actionTypes'
import defaultsDeep from 'lodash/defaultsDeep'
import { isHardFail } from 'lib/action'

interface BookmarkDataState {
  content: string
  nodeIds: string[]
}

interface BookmarkState {
  data: BookmarkDataState
  syncData: BookmarkDataState | null
  local: boolean
  isFetching: boolean
  deleted: boolean
  editing: boolean
  failed: boolean
  id: string
}

interface BookmarkInput {
  id?: string
  content?: string
  nodeIds?: string[]
  [key: string]: unknown
}

interface BookmarkAction {
  type?: string
  meta?: {
    isRetry?: boolean
  }
  payload?: {
    content?: string
    nodeIds?: string[]
    data?: BookmarkInput
  }
}

const initialState: BookmarkState = {
  data: {
    content: '',
    nodeIds: [],
  },
  syncData: null,
  local: true,
  isFetching: false,
  deleted: false,
  editing: false,
  failed: true,
  id: '',
}


const single = (
  state: BookmarkState = initialState,
  action: BookmarkAction,
  bookmark: BookmarkInput = {}
): BookmarkState => {
  state.id = bookmark.id ?? state.id
  switch (action.type) {
    case actionTypes.BOOKMARKS.ONE.START_EDITING:
      return defaultsDeep(
        {
          editing: true
        },
        state
      )
    case actionTypes.BOOKMARKS.ONE.STOP_EDITING:
      return defaultsDeep(
        {
          editing: false
        },
        state
      )
    case actionTypes.BOOKMARKS.ONE.CREATE.REQUEST: {
      let data: Partial<BookmarkDataState> = {}
      if (typeof action.payload?.content === 'string') data.content = action.payload.content
      if (Array.isArray(action.payload?.nodeIds)) data.nodeIds = action.payload.nodeIds
      if (action.meta?.isRetry) data = {}
      return defaultsDeep(
        {
          data: data,
          local: true,
          isFetching: true
        },
        state
      )
    }
    case actionTypes.BOOKMARKS.ONE.CREATE.RECEIVE: {
      const payloadData = action.payload?.data
      const data = defaultsDeep(
        {
          content: payloadData?.content,
          nodeIds: payloadData?.nodeIds
        },
        state.data
      )
      return defaultsDeep(
        {
          syncData: data,
          isFetching: false,
          local: false,
          failed: false
        },
        state
      )
    }
    case actionTypes.BOOKMARKS.ONE.CREATE.FAIL: {
      const failedState = defaultsDeep(
        {
          isFetching: false,
          failed: true
        },
        state
      )
      if (isHardFail(action)) {
        failedState.data = state.syncData
        failedState.failed = false
      }
      return failedState
    }
    case actionTypes.BOOKMARKS.ONE.EDIT.REQUEST: {
      let data: Partial<BookmarkDataState> = {}
      if (typeof action.payload?.content === 'string') data.content = action.payload.content
      if (Array.isArray(action.payload?.nodeIds)) data.nodeIds = action.payload.nodeIds
      if (action.meta?.isRetry) data = {}
      return defaultsDeep(
        {
          data: data,
          isFetching: true
        },
        state
      )
    }
    case actionTypes.BOOKMARKS.ONE.EDIT.RECEIVE: {
      const payloadData = action.payload?.data
      const data = defaultsDeep(
        {
          content: payloadData?.content,
          nodeIds: payloadData?.nodeIds
        },
        state.data
      )
      return defaultsDeep(
        {
          syncData: data,
          isFetching: false,
          failed: false
        },
        state
      )
    }
    case actionTypes.BOOKMARKS.ONE.EDIT.FAIL: {
      const failedState = defaultsDeep(
        {
          isFetching: false,
          failed: true
        },
        state
      )
      if (isHardFail(action)) {
        failedState.data = state.syncData
        failedState.failed = false
      }
      return failedState
    }
    case actionTypes.BOOKMARKS.ONE.DELETE.REQUEST:
      return defaultsDeep(
        {
          isFetching: true
        },
        state
      )
    case actionTypes.BOOKMARKS.ONE.DELETE.FAIL: {
      const failedState = defaultsDeep(
        {
          isFetching: false,
          deleted: true
        },
        state
      )
      if (isHardFail(action)) {
        failedState.deleted = false
        failedState.failed = false
      }
      return failedState
    }
    case actionTypes.BOOKMARKS.MULTI.GET_ALL.RECEIVE: {
      const data = defaultsDeep(
        {
          content: bookmark.content,
          nodeIds: bookmark.nodeIds
        },
        state.data
      )
      return defaultsDeep(
        {
          syncData: data,
          data: data
        },
        state
      )
    }
    default:
      return state
  }
}

export default single
